import {
	HeaderActions as Actions,
	Avatar,
	Badge,
	PopoverControlButton,
} from '@audio-network/componentlibrary'
import { AddPerson, Chat } from '@audio-network/componentlibrary/icons'

import AddToTeam from '@/components/shared/AddToTeam/AddToTeam'
import clsx from 'clsx'
import dynamic from 'next/dynamic'
import styles from './HeaderActions.module.css'
import { useHeaderActions } from './HeaderActions.hooks'
import { useMemo } from 'react'
import { useSession } from '@/context/Session'
import { useTranslation } from 'next-i18next'

const ServiceMenu = dynamic(() => import('../ServiceMenu/ServiceMenu'), {
	ssr: false,
})

const AccountMenu = dynamic(() => import('../AccountMenu/AccountMenu'), {
	ssr: false,
})

const LoggedOutActions = dynamic(
	() => import('../LoggedOutActions/LoggedOutActions'),
	{
		ssr: false,
	}
)

const HeaderActions = (): JSX.Element => {
	const { t } = useTranslation(['navigation'])
	const { loadingSession, user } = useSession()

	const {
		showAddToTeam,
		handleAddToTeamButtonClick,
		handleServiceMenuButtonClick,
		handleAccountMenuButtonClick,
		showServiceMenu,
		itemsCount,
	} = useHeaderActions({ user })

	const initials = useMemo(() => {
		if (!user) return ''
		return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`
	}, [user])

	if (loadingSession) {
		return null
	}

	if (!user) {
		return (
			<Actions id="header-actions">
				<LoggedOutActions />
			</Actions>
		)
	}

	return (
		<Actions id="header-actions">
			<div className={styles.popoverControlButtonWrapper}>
				{showAddToTeam && (
					<>
						<PopoverControlButton
							aria-label={t('add-to-team-cta')}
							data-testid="add-to-team-button"
							className={clsx(styles.actionButton, styles.anchor)}
							popovertarget="add-to-team-popover"
							id="add-to-team-button"
							onClick={handleAddToTeamButtonClick}
						>
							<AddPerson />
							<span aria-hidden="true" className={styles.actionButtonText}>
								{t('add-to-team-cta')}
							</span>
						</PopoverControlButton>

						<AddToTeam />
					</>
				)}
				{showServiceMenu && (
					<>
						<PopoverControlButton
							aria-label={t('need-support')}
							data-testid="service-menu-button"
							id="service-menu-button"
							onClick={handleServiceMenuButtonClick}
							popovertarget="service-menu"
							className={clsx(styles.actionButton, styles.anchor)}
						>
							<Chat />
							<span aria-hidden="true" className={styles.actionButtonText}>
								{t('need-support')}
							</span>
						</PopoverControlButton>

						<ServiceMenu user={user} />
					</>
				)}
				<PopoverControlButton
					aria-label={t('account-actions')}
					data-testid="account-menu-button"
					className={styles.actionButton}
					onClick={handleAccountMenuButtonClick}
					popovertarget="account-menu"
					id="account-menu-button"
				>
					<Badge badgeContent={itemsCount} data-testid="cart-badge">
						<Avatar
							aria-label={initials}
							className={styles.anchor}
							initials={initials}
						/>
					</Badge>
				</PopoverControlButton>

				<AccountMenu itemsCount={itemsCount} user={user} />
			</div>
		</Actions>
	)
}

export default HeaderActions
